import {
    MappingType,
    IDataFlowExpression,
    IViolation,
    ModelMarker
} from 'shell-models-lib';
import { DataMapperMapping } from 'app/model/adfDataMapper/dataMapperMapping';
import { AdfDataMapper } from 'app/model/adfDataMapper/adfDataMapper';
import { MapperAttributeMapping } from 'app/model/adfDataMapper/mapperAttributeMapping';
import { MapperAttributeReference } from 'app/model/adfDataMapper/mapperAttributeReference';
import { format } from 'app/shared/utils/string.util';
import { IMapperConnectionInfo, IMapperTable } from 'app/model/adfDataMapper/IMapperTable';
import { AdfDataMapperEntityType } from 'app/model/adfDataMapper/adfDataMapper.const';
import { MappingTargetConnectionInfo } from 'app/model/adfDataMapper/mappingTargetConnectionInfo';
import { MapperConnection } from 'app/model/adfDataMapper/mapperConnection';
import { MappingSourceConnectionInfo } from 'app/model/adfDataMapper/mappingSourceConnectionInfo';
import { MapperPolicy } from 'app/model/adfDataMapper/mapperPolicy';
import { MapperFrequencyMode } from 'app/model/adfDataMapper/mapperPolicyRecurrence';
import { IMapperViolationContext, IMapperViolationDetail, MapperViolationCategory, MapperViolationType } from 'app/model/adfDataMapper/mapperViolationInterfaces';
import { MapperTableReference } from 'app/model/adfDataMapper/mapperTableReference';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { DataFlowColumn } from 'app/model/dataset/dataflowcolumn';
import { DataFlowDataType, DecimalDataFlowType, LongDataFlowType, StringDataFlowType } from 'worker/app-workers/shared/models/dataflow-datatype';
import { MapperConnectionReference } from 'app/model/adfDataMapper/mapperConnectionReference';
import { DataFlowExpressionFunctions } from 'app/model/dataflow/dataflow-expression-functions';
import { DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { MapperStagingSettings } from 'app/model/adfDataMapper/mapperStagingSettings';
import { supportedSqlDatasetFormatToDatasetSettingMap } from 'app/components/create-datamapper/utils/sqlentity-component-utils';
import { DslConnectorConstants } from 'app/model/adapters/mappingArtifact/DslConnectorConstants';
import * as tableUtils from 'app/model/adfDataMapper/mapperTable.utils';

export class AdfDataMapperValidations {

    private static sinksSupportingComplexDataTypes = new Set([DatasetFormatType.Orc, DatasetFormatType.Json, DatasetFormatType.Avro]);

    public static validateAdfDataMapper(adfDataMapper: AdfDataMapper): IViolation[] | undefined {
        const violations: IViolation[] = [];

        if (!!adfDataMapper) {
            if (ModelMarker.isDirty(adfDataMapper) && (adfDataMapper.isInInterimState || adfDataMapper.runStatus !== undefined && adfDataMapper.runStatus !== 'Stopped')) {
                violations.push({
                    message: () => format(stringResources.Violation_UpdateWhileRunning)
                });
            }
            AdfDataMapperValidations._addNameViolations(violations, adfDataMapper.name);
            AdfDataMapperValidations._addNoTableMappingViolation(violations, adfDataMapper.targetConnectionsInfo);
            AdfDataMapperValidations._addTargetConnectionsInfoViolations(violations, adfDataMapper);
            AdfDataMapperValidations._addSourceConnectionsInfoViolations(violations, adfDataMapper.sourceConnectionsInfo);
            AdfDataMapperValidations._addPolicyViolations(violations, adfDataMapper.policy);
            AdfDataMapperValidations._addStagingSettingsViolations(violations, adfDataMapper);
        }

        return violations;
    }

    public static validateDataMapping(dataMapperMapping: DataMapperMapping): IViolation[] | undefined {
        const violations: IViolation[] = [];
        const matchedTargetConnectionInfo = AdfDataMapperValidations._getMatchingTargetConnectionInfo(dataMapperMapping);

        if (!!matchedTargetConnectionInfo) {
            const mapper = dataMapperMapping.owner as AdfDataMapper;
            const matchedTargetConnectionIndex = mapper.targetConnectionsInfo.indexOf(matchedTargetConnectionInfo);
            const matchedDataMappingIndex = matchedTargetConnectionInfo.dataMapperMappings.indexOf(dataMapperMapping);

            AdfDataMapperValidations._addAttributeMappingViolations(violations, matchedTargetConnectionIndex, dataMapperMapping, matchedDataMappingIndex);
        }
        else {
            AdfDataMapperValidations._addAttributeMappingViolations(violations, -1, dataMapperMapping, -1);
        }

        violations.forEach((violation) => {
            violation.redirectTo = {
                entity: dataMapperMapping.owner ? dataMapperMapping.owner as AdfDataMapper : undefined
            };
        });
        return violations;
    }

    private static _addNameViolations(violations: IViolation[], name: string): void {
        if (!name) {
            violations.push({
                message: () => format(stringResources.Violation_InvalidPolicyDetails)
            });
        }
    }

    private static _addSourceConnectionsInfoViolations(violations: IViolation[], sourceConnectionsInfo: MappingSourceConnectionInfo[]): void {
        AdfDataMapperValidations._addEmptyConnectionsInfoViolations(violations, sourceConnectionsInfo, 'source');

        sourceConnectionsInfo?.forEach((sourceConnectionInfo, connectionIndex) => {
            AdfDataMapperValidations._addConnectionTablesViolations(violations, connectionIndex, 0, sourceConnectionInfo.tables, 'source');
            AdfDataMapperValidations._addConnectionViolations(violations, connectionIndex, sourceConnectionInfo.connection, 'source');
        });
    }

    private static _addTargetConnectionsInfoViolations(violations: IViolation[], adfDataMapper: AdfDataMapper): void {
        const targetConnectionsInfo = adfDataMapper.targetConnectionsInfo;
        AdfDataMapperValidations._addEmptyConnectionsInfoViolations(violations, targetConnectionsInfo, 'sink');
        AdfDataMapperValidations._addDuplicateSourceTargetTableMappingsViolations(violations, targetConnectionsInfo);

        let numPreviousMappingsForCurrentConnection = 0;
        targetConnectionsInfo?.forEach((targetConnectionInfo, targetConnectionIndex) => {
            AdfDataMapperValidations._addConnectionTablesViolations(violations, targetConnectionIndex, numPreviousMappingsForCurrentConnection, targetConnectionInfo.tables, 'sink');
            AdfDataMapperValidations._addConnectionViolations(violations, targetConnectionIndex, targetConnectionInfo.connection, 'sink');
            AdfDataMapperValidations._addTableMappingViolations(violations, adfDataMapper, targetConnectionIndex, numPreviousMappingsForCurrentConnection, targetConnectionInfo.dataMapperMappings);

            numPreviousMappingsForCurrentConnection += targetConnectionInfo.dataMapperMappings.length;
        });
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    private static _addStagingSettingsViolations(violations: IViolation[], adfDataMapper: AdfDataMapper): void {
        const stagingSettings: MapperStagingSettings = adfDataMapper.stagingSettings;
        adfDataMapper.sourceConnectionsInfo.forEach((connectionInfo) => this._addStagingSettingsViolationsForAConnectionInfo(violations, connectionInfo, stagingSettings, 'source'));
        adfDataMapper.targetConnectionsInfo.forEach((connectionINfo) => this._addStagingSettingsViolationsForAConnectionInfo(violations, connectionINfo, stagingSettings, 'target'));
    }

    private static _addStagingSettingsViolationsForAConnectionInfo(violations: IViolation[], connectionInfo: IMapperConnectionInfo, stagingSettings: MapperStagingSettings, connectionType: string): void{
        const context: IMapperViolationContext = {
            violationCategory: MapperViolationCategory.StagingSettings,
            violationType: MapperViolationType.StagingSettings,
        };
        connectionInfo.tables.forEach((table)=>{
            if(this.isStagingEnabled(table.dslConnectorProperties, connectionInfo.connection.commonDslConnectorProperties)){
                if(!stagingSettings?.stagingFolderPath){
                    violations.push({
                        context,
                        message: () => format(stringResources.Violation_StagingSettings_FolderPath_NotConfigured, connectionType, table.tableName)
                    });
                }
                if(!stagingSettings?.stagingLinkedService){
                    violations.push({
                        context,
                        message: () => format(stringResources.Violation_StagingSettings_LinkedService_NotConfigured, connectionType, table.tableName)
                    });
                }
            }
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static isStagingEnabled(dslPropertiesMap: Map<string, any>, commongDslPropertiesMap: Map<string, any>): boolean {
        const mergeDslProperties = tableUtils.mergeDictionaries(dslPropertiesMap, commongDslPropertiesMap);
        return tableUtils.getDslPropertyValue(mergeDslProperties, 'staged') === true;
    }

    private static _addPolicyViolations(violations: IViolation[], policy: MapperPolicy): void {
        if (!policy ||
            !policy.mode ||
            policy.mode === MapperFrequencyMode.None ||
            (policy.mode === MapperFrequencyMode.Realtime && (!policy.recurrence || !policy.recurrence.frequency))) {

            const context: IMapperViolationContext = {
                violationCategory: MapperViolationCategory.TableMappingCanvas,
                violationType: MapperViolationType.Policy,
            };

            violations.push({
                context,
                message: () => format(stringResources.Violation_InvalidPolicyDetails)
            });
        }
    }

    private static _addConnectionViolations(
        violations: IViolation[],
        connectionIndex: number,
        connection: MapperConnection,
        entityType: AdfDataMapperEntityType): void {

        if (!connection || connection.getConnectionReference()?.isValidReference() !== true) {
            const context: IMapperViolationContext = {
                violationCategory: MapperViolationCategory.TableMappingCanvas,
                violationType: MapperViolationType.ConnectionConfiguration,
                targetConnectionIndex: connectionIndex,
                entityType
            };

            violations.push({
                context,
                message: () => format(stringResources.Violation_InvalidConnectionDetails, entityType === 'source' ? stringResources.Source_Label : stringResources.Target)
            });
        }
    }

    private static _addConnectionTablesViolations(
        violations: IViolation[],
        connectionIndex: number,
        numPreviousMappingsForCurrentConnection: number,
        tables: IMapperTable[],
        entityType: AdfDataMapperEntityType): void {

        AdfDataMapperValidations._addNoTablesViolation(violations, connectionIndex, tables, entityType);

        tables?.forEach((table, tableIndex) => {
            AdfDataMapperValidations._addConnectionTableViolations(violations, connectionIndex, table, numPreviousMappingsForCurrentConnection + tableIndex, entityType);
        });
    }

    private static _addNoTablesViolation(
        violations: IViolation[],
        connectionIndex: number,
        tables: IMapperTable[],
        entityType: AdfDataMapperEntityType): void {
        if (!tables || tables.length === 0) {
            const context: IMapperViolationContext = {
                violationCategory: MapperViolationCategory.TableMappingCanvas,
                violationType: MapperViolationType.NoTablesForConnection,
                targetConnectionIndex: connectionIndex,
                entityType
            };

            violations.push({
                context,
                message: () => format(stringResources.Violation_EmptyTablesInfo, entityType === 'source' ? stringResources.Source_Label : stringResources.Target)
            });
        }
    }

    private static _addConnectionTableViolations(
        violations: IViolation[],
        connectionIndex: number,
        table: IMapperTable,
        tableIndex: number,
        entityType: AdfDataMapperEntityType): void {

        const context: IMapperViolationContext = {
            violationCategory: MapperViolationCategory.TableMappingCanvas,
            violationType: MapperViolationType.TableConfiguration,
            targetConnectionIndex: connectionIndex,
            entityType,
            tableIndex
        };

        if (!table.datasetType) {
            violations.push({
                context,
                message: () => format(stringResources.Violation_InvalidTableDatasetType, table.tableName, entityType === 'source' ? stringResources.Source_Label : stringResources.Target)
            });
        }
        if (!table.dslConnectorProperties || table.dslConnectorProperties.size === 0) {
            violations.push({
                context,
                message: () => format(stringResources.Violation_InvalidTableDslConnectorProperties, table.tableName, entityType === 'source' ? stringResources.Source_Label : stringResources.Target)
            });
        }
    }

    private static _addDuplicateSourceTargetTableMappingsViolations(
        violations: IViolation[],
        allTargetConnectionsInfo: MappingTargetConnectionInfo[]): void {

        const usedSourceTableMap = new Map<string, { targetConnectionIndex: number, dataMappingIndex: number }>();

        let numPreviousMappingsForCurrentConnection = 0;
        allTargetConnectionsInfo.forEach((targetConnectionInfo, targetConnectionIndex) => {
            // detect duplicate source table mappings
            this._addDuplicateSourceTableMappingsViolations(violations, targetConnectionIndex, targetConnectionInfo, allTargetConnectionsInfo, numPreviousMappingsForCurrentConnection, usedSourceTableMap);

            // detect duplicate target table mappings
            const usedTargetTableMap = new Map<string, number>();
            this._addDuplicateTargetTableMappingsViolations(violations, targetConnectionIndex, targetConnectionInfo, numPreviousMappingsForCurrentConnection, usedTargetTableMap);

            numPreviousMappingsForCurrentConnection += targetConnectionInfo.dataMapperMappings.length;
        });
    }

    private static _addDuplicateSourceTableMappingsViolations(
        violations: IViolation[],
        targetConnectionIndex: number,
        targetConnectionInfo: MappingTargetConnectionInfo,
        allTargetConnectionsInfo: MappingTargetConnectionInfo[],
        numPreviousMappingsForCurrentConnection: number,
        usedSourceTableMap: Map<string, { targetConnectionIndex: number, dataMappingIndex: number }>): void {

        targetConnectionInfo.dataMapperMappings.forEach((dataMapperMapping, dataMapperMappingIndex) => {
            if (!!dataMapperMapping.sourceTable?.tableName && dataMapperMapping.sourceConnectionReference?.isValidReference() === true) {
                const sourceTableReference = new MapperTableReference(dataMapperMapping.sourceTable, dataMapperMapping.sourceConnectionReference);
                const sourceTableUniqueAlias = sourceTableReference.getUniqueAlias();
                if (usedSourceTableMap.has(sourceTableUniqueAlias)) {
                    const numPreviousMappingsForPreviousConnection = AdfDataMapperValidations._getNumPreviousMappings(
                        usedSourceTableMap.get(sourceTableUniqueAlias).targetConnectionIndex,
                        allTargetConnectionsInfo);
                    const context: IMapperViolationContext = {
                        violationCategory: MapperViolationCategory.TableMappingCanvas,
                        violationType: MapperViolationType.DuplicateSourceMapping,
                        duplicateTargetConnectionIndex: usedSourceTableMap.get(sourceTableUniqueAlias).targetConnectionIndex,
                        duplicateTableMappingIndex: numPreviousMappingsForPreviousConnection + usedSourceTableMap.get(sourceTableUniqueAlias).dataMappingIndex,
                        targetConnectionIndex,
                        tableMappingIndex: numPreviousMappingsForCurrentConnection + dataMapperMappingIndex,
                        sourceTableName: dataMapperMapping.sourceTable.tableName
                    };

                    const previousTargetConnectionInfo = allTargetConnectionsInfo[usedSourceTableMap.get(sourceTableUniqueAlias).targetConnectionIndex];
                    const previousDataMapperMapping = previousTargetConnectionInfo.dataMapperMappings[usedSourceTableMap.get(sourceTableUniqueAlias).dataMappingIndex];
                    const previousTargetTableName = previousDataMapperMapping.targetTable?.tableName;

                    const currentConnectionName = targetConnectionInfo.connection?.getConnectionReference()?.connectionName;
                    const previousConnectionName = previousTargetConnectionInfo.connection?.getConnectionReference()?.connectionName;
                    violations.push({
                        context,
                        message: () => format(stringResources.Violation_DuplicateSourceTableMapping, dataMapperMapping.sourceTable.tableName,
                            dataMapperMapping.targetTable?.tableName, currentConnectionName, previousTargetTableName, previousConnectionName)
                    });
                }
                else {
                    usedSourceTableMap.set(sourceTableUniqueAlias, { targetConnectionIndex, dataMappingIndex: dataMapperMappingIndex });
                }
            }
        });
    }

    private static _addDuplicateTargetTableMappingsViolations(
        violations: IViolation[],
        targetConnectionIndex: number,
        targetConnectionInfo: MappingTargetConnectionInfo,
        numPreviousMappingsForCurrentConnection: number,
        usedTargetTableMap: Map<string, number>): void {

        targetConnectionInfo.dataMapperMappings.forEach((dataMapperMapping, dataMapperMappingIndex) => {
            if (!!dataMapperMapping.targetTable?.tableName) {
                if (usedTargetTableMap.has(dataMapperMapping.targetTable.tableName)) {
                    const context: IMapperViolationContext = {
                        violationCategory: MapperViolationCategory.TableMappingCanvas,
                        violationType: MapperViolationType.DuplicateTargetMapping,
                        duplicateTableMappingIndex: usedTargetTableMap.get(dataMapperMapping.targetTable.tableName),
                        targetConnectionIndex,
                        tableMappingIndex: numPreviousMappingsForCurrentConnection + dataMapperMappingIndex,
                        targetTableName: dataMapperMapping.targetTable.tableName
                    };

                    const previousDataMapperMapping = targetConnectionInfo.dataMapperMappings[usedTargetTableMap.get(dataMapperMapping.targetTable.tableName)];
                    const connectionName = targetConnectionInfo.connection?.getConnectionReference()?.connectionName;
                    const previousSourceTableName = previousDataMapperMapping.sourceTable.tableName;
                    violations.push({
                        context,
                        message: () => format(stringResources.Violation_DuplicateTargetTableMapping,
                            dataMapperMapping.targetTable?.tableName, connectionName, dataMapperMapping.sourceTable.tableName, previousSourceTableName)
                    });
                }
                else {
                    usedTargetTableMap.set(dataMapperMapping.targetTable.tableName, dataMapperMappingIndex);
                }
            }
        });
    }

    private static _addEmptyConnectionsInfoViolations(
        violations: IViolation[],
        connectionsInfo: IMapperConnectionInfo[],
        entityType: AdfDataMapperEntityType): void {

        if (!connectionsInfo || connectionsInfo.length === 0) {
            const context: IMapperViolationContext = {
                violationCategory: MapperViolationCategory.TableMappingCanvas,
                violationType: MapperViolationType.NoConnections,
                entityType
            };

            violations.push({
                context,
                message: () => format(stringResources.Violation_EmptyConnectionsInfo, entityType === 'source' ? stringResources.Source_Label : stringResources.Target)
            });
        }
    }

    private static _addNoTableMappingViolation(
        violations: IViolation[],
        targetConnectionsInfo: MappingTargetConnectionInfo[]): void {

        let someMappingExists = false;
        for (const targetConnection of targetConnectionsInfo) {
            if (!!targetConnection && targetConnection.dataMapperMappings?.length > 0) {
                someMappingExists = true;
                break;
            }
        }

        if (!someMappingExists) {
            const context: IMapperViolationContext = {
                violationCategory: MapperViolationCategory.TableMappingCanvas,
                violationType: MapperViolationType.NoTableMapping
            };

            violations.push({
                context,
                message: () => format(stringResources.Violation_NoTableMappingExists)
            });
        }
    }

    private static _addTableMappingViolations(
        violations: IViolation[],
        adfDataMapper: AdfDataMapper,
        targetConnectionIndex: number,
        numPreviousMappingsForCurrentConnection: number,
        dataMapperMappings: DataMapperMapping[]): void {

        const targetConnection = adfDataMapper.targetConnectionsInfo[targetConnectionIndex].connection;
        const mappingViolationDetails = AdfDataMapperValidations._getTableMappingsViolationDetails(dataMapperMappings, adfDataMapper, targetConnection);
        const connectionName = targetConnection?.getConnectionReference()?.connectionName;
        if (!!mappingViolationDetails) {
            mappingViolationDetails.forEach((violationDetail) => {
                const context: IMapperViolationContext = {
                    violationCategory: MapperViolationCategory.TableMappingCanvas,
                    targetConnectionIndex,
                    targetTableName: violationDetail.targetTable,
                    sourceTableName: violationDetail.sourceTable,
                    tableMappingIndex: numPreviousMappingsForCurrentConnection + violationDetail.tableMappingIndex,
                    violationType: violationDetail.violationType
                };

                if (violationDetail.violationType === MapperViolationType.NoTableMapping) {
                    violations.push({
                        context,
                        message: () => format(stringResources.Violation_NoTableMappingProvided, connectionName)
                    });
                }
                else if (violationDetail.violationType === MapperViolationType.InvalidMapping) {
                    violations.push({
                        context,
                        message: () => format(stringResources.Violation_InvalidTableMapping, connectionName)
                    });
                }
                else if (violationDetail.violationType === MapperViolationType.NoTargetTableMapping) {
                    violations.push({
                        context,
                        message: () => format(stringResources.Violation_TargetNotSelectedInTableMapping, violationDetail.sourceTable, connectionName)
                    });
                }
                else if (violationDetail.violationType === MapperViolationType.NoSourceTableMapping) {
                    violations.push({
                        context,
                        message: () => format(stringResources.Violation_SourceNotSelectedInTableMapping, violationDetail.targetTable, connectionName)
                    });
                }
                else if (violationDetail.violationType === MapperViolationType.NoColumnMapping) {
                    violations.push({
                        context,
                        message: () => format(stringResources.Violation_NoColumnMappingProvided, violationDetail.sourceTable, violationDetail.targetTable, connectionName)
                    });
                }
                else if(violationDetail.violationType === MapperViolationType.NoKeysSelected) {
                    violations.push({
                        context,
                        message: () => format(stringResources.Violation_NoTargetKeysProvided, violationDetail.targetTable)
                    });
                }
            });
        }
    }

    private static _addAttributeMappingViolations(
        violations: IViolation[],
        targetConnectionIndex: number,
        dataMapperMapping: DataMapperMapping,
        dataMapperMappingIndex: number): void {
        const mappingViolationDetails = AdfDataMapperValidations._getAttributeMappingViolationDetails(dataMapperMapping);

        if (!!mappingViolationDetails) {
            mappingViolationDetails.forEach((violationDetail) => {
                const context: IMapperViolationContext = {
                    violationCategory: MapperViolationCategory.ColumnMappingCanvas,
                    targetConnectionIndex,
                    targetTableName: dataMapperMapping.targetTable?.tableName,
                    sourceTableName: dataMapperMapping.sourceTable?.tableName,
                    tableMappingIndex: dataMapperMappingIndex,
                    violationType: violationDetail.violationType,
                    attributeMappingIndex: violationDetail.attributeMappingIndex,
                    previousAttributeMappingIndex: violationDetail.previousAttributeMappingIndex,
                    mappingMethod: violationDetail.mappingMethod,
                    targetAttributeName: violationDetail.targetAttribute,
                    entityType: violationDetail.entityType
                };

                if (violationDetail.violationType === MapperViolationType.MappingMethod) {
                    violations.push({
                        context,
                        message: () => format(stringResources.Violation_InvalidMappingType, violationDetail.targetAttribute, dataMapperMapping.targetTable.tableName)
                    });
                }
                else if (violationDetail.violationType === MapperViolationType.SourceColumn) {
                    violations.push({
                        context,
                        message: () => format(stringResources.Violation_InvalidSourceColumn, violationDetail.targetAttribute, dataMapperMapping.targetTable.tableName)
                    });
                }
                else if (violationDetail.violationType === MapperViolationType.TargetColumn) {
                    violations.push({
                        context,
                        message: () => format(stringResources.Violation_InvalidTargetColumn, dataMapperMapping.targetTable.tableName)
                    });
                }
                else if (violationDetail.violationType === MapperViolationType.MappingCombination) {
                    violations.push({
                        context,
                        message: () => format(stringResources.Violation_InvalidMappingCombination, dataMapperMapping.targetTable.tableName)
                    });
                }
                else if (violationDetail.violationType === MapperViolationType.DuplicateTargetMapping) {
                    violations.push({
                        context,
                        message: () => format(stringResources.Violation_DuplicateTargetColumnMapping, violationDetail.targetAttribute, dataMapperMapping.targetTable.tableName)
                    });
                }
                else if (violationDetail.violationType === MapperViolationType.OrphanSourceColumn) {
                    violations.push({
                        context,
                        message: () => format(stringResources.Violation_OrphanColumn, stringResources.SourceInSmall, violationDetail.sourceAttribute, stringResources.SourceInSmall, dataMapperMapping.sourceTable.tableName)
                    });
                }
                else if (violationDetail.violationType === MapperViolationType.OrphanTargetColumn) {
                    violations.push({
                        context,
                        message: () => format(stringResources.Violation_OrphanColumn, stringResources.TargetInSmall, violationDetail.targetAttribute, stringResources.TargetInSmall, dataMapperMapping.targetTable.tableName)
                    });
                }
                else if (violationDetail.violationType === MapperViolationType.InvalidSourceColumnDataType) {
                    violations.push({
                        context,
                        message: () => format(
                            stringResources.Violation_InvalidColumnDataType,
                            violationDetail.functionName,
                            violationDetail.expectedDataTypes.join(', '),
                            violationDetail.sourceAttribute,
                            stringResources.SourceInSmall,
                            violationDetail.sourceTable,
                            violationDetail.sourceDataType)
                    });
                }
                else if (violationDetail.violationType === MapperViolationType.InvalidTargetColumnDataType) {
                    violations.push({
                        context,
                        message: () => format(
                            stringResources.Violation_InvalidColumnDataType,
                            violationDetail.functionName,
                            violationDetail.expectedDataTypes.join(', '),
                            violationDetail.targetAttribute,
                            stringResources.TargetInSmall,
                            violationDetail.targetTable,
                            violationDetail.targetDataType)
                    });
                }
                else if (violationDetail.violationType === MapperViolationType.InvalidTargetForComplexDataType) {
                    violations.push({
                        context,
                        message: () => format(
                            stringResources.Violation_InvalidTargetForComplexDataType,
                            violationDetail.targetAttribute,
                            violationDetail.targetTable,
                            violationDetail.tableType,
                            Array.from(AdfDataMapperValidations.sinksSupportingComplexDataTypes).join(', '))
                    });
                }
                else if (violationDetail.violationType === MapperViolationType.InvalidMappingForComplexDataType) {
                    violations.push({
                        context,
                        message: () => format(
                            stringResources.Violation_InvalidComplexColumnMapping,
                            violationDetail.sourceAttribute,
                            violationDetail.sourceDataType,
                            violationDetail.targetAttribute,
                            violationDetail.targetDataType)
                    });
                }
                else if (violationDetail.violationType === MapperViolationType.InvalidMethodForContinuousMapper) {
                    violations.push({
                        context,
                        message: () => format(
                            stringResources.Violation_InvalidMethodForContinupusMapper,
                            violationDetail.targetAttribute,
                            dataMapperMapping.targetTable.tableName)
                    });
                }
            });
        }
    }

    private static _getTableMappingsViolationDetails(
        dataMapperMappings: DataMapperMapping[],
        adfDataMapper: AdfDataMapper,
        targetConnection: MapperConnection): IMapperViolationDetail[] {
        const violationsDetails: IMapperViolationDetail[] = [];

        if (!dataMapperMappings) {
            violationsDetails.push({
                violationType: MapperViolationType.NoTableMapping
            });

            return violationsDetails;
        }

        dataMapperMappings.forEach((dataMapperMapping, dataMappingIndex) => {
            const sourceConnectionInfo = tableUtils.getTableConnectionInfo(adfDataMapper.sourceConnectionsInfo, dataMapperMapping.sourceConnectionReference);
            this._addTableMappingViolationDetails(
                violationsDetails,
                dataMappingIndex,
                dataMapperMapping,
                sourceConnectionInfo.connection,
                targetConnection);
        });

        return violationsDetails;
    }

    private static _addTableMappingViolationDetails(
        violationsDetails: IMapperViolationDetail[],
        dataMappingIndex: number,
        dataMapperMapping: DataMapperMapping,
        sourceConnection: MapperConnection,
        targetConnection: MapperConnection): void {
        if (!dataMapperMapping) {
            violationsDetails.push({
                violationType: MapperViolationType.NoTableMapping,
                tableMappingIndex: dataMappingIndex
            });
            return;
        }

        const isTargetTableNotSelected = !dataMapperMapping.targetTable?.tableName;
        const isSourceTableNotSelected = !dataMapperMapping.sourceTable?.tableName || dataMapperMapping.sourceConnectionReference?.isValidReference() !== true;

        if (isTargetTableNotSelected || isSourceTableNotSelected) {
            if (isTargetTableNotSelected && isSourceTableNotSelected) {
                violationsDetails.push({
                    violationType: MapperViolationType.InvalidMapping,
                    tableMappingIndex: dataMappingIndex
                });
            }
            else if (isTargetTableNotSelected) {
                violationsDetails.push({
                    violationType: MapperViolationType.NoTargetTableMapping,
                    tableMappingIndex: dataMappingIndex,
                    sourceTable: dataMapperMapping.sourceTable?.tableName
                });
            }
            else {
                violationsDetails.push({
                    violationType: MapperViolationType.NoSourceTableMapping,
                    tableMappingIndex: dataMappingIndex,
                    targetTable: dataMapperMapping.targetTable?.tableName
                });
            }
        }
        else if (!dataMapperMapping.isSourceTargetDrifted()) {
            if (AdfDataMapperValidations._isAttributeMappingsEmpty(dataMapperMapping)) {
                violationsDetails.push({
                    violationType: MapperViolationType.NoColumnMapping,
                    tableMappingIndex: dataMappingIndex,
                    targetTable: dataMapperMapping.targetTable?.tableName,
                    sourceTable: dataMapperMapping.sourceTable?.tableName
                });
            }
        }

        if (AdfDataMapperValidations._isNativeCdcSqlSource(dataMapperMapping.sourceTable, sourceConnection)) {
            if (!AdfDataMapperValidations._hasKeysForTarget(dataMapperMapping.targetTable, targetConnection)) {
                violationsDetails.push({
                    violationType: MapperViolationType.NoKeysSelected,
                    tableMappingIndex: dataMappingIndex,
                    targetTable: dataMapperMapping.targetTable?.tableName,
                    sourceTable: dataMapperMapping.sourceTable?.tableName
                });
            }
        }
    }

    private static _getAttributeMappingViolationDetails(dataMapperMapping: DataMapperMapping): IMapperViolationDetail[] {
        const violationsDetails: IMapperViolationDetail[] = [];

        if (dataMapperMapping.attributeMappings && dataMapperMapping.attributeMappings.length > 0) {
            AdfDataMapperValidations._addDuplicateTargetAttributeMappingViolations(violationsDetails, dataMapperMapping);

            let containsDerivedTransformations = false;
            let containsAggregateTransformation = false;
            let invalidMappingCombinationAttributeMappingIndex = -1;
            let invalidMappingCombinationAttributeMapping: MapperAttributeMapping;
            let basicMappingIndex = 0;
            let advancedMappingIndex = 0;
            const isMapperContinuous = (dataMapperMapping.owner as AdfDataMapper).isContinuous();

            dataMapperMapping.attributeMappings.forEach((attributeMapping, targetColumnIndex) => {

                let sourceColumnIndex = targetColumnIndex;
                let isMappingTypeValid = false;
                let areSourceTargetColumnsProper = false;

                // no mapping type is specified
                if (!AdfDataMapperValidations._isMappingTypeValid(attributeMapping.mappingType)) {
                    violationsDetails.push({
                        violationType: MapperViolationType.MappingMethod,
                        attributeMappingIndex: targetColumnIndex,
                        targetAttribute: attributeMapping.name
                    });
                }
                else {
                    isMappingTypeValid = true;
                    if (attributeMapping.mappingType === MappingType.ADVANCED.toString()) {
                        sourceColumnIndex = advancedMappingIndex;
                        advancedMappingIndex++;
                    } else {
                        sourceColumnIndex = basicMappingIndex;
                        basicMappingIndex++;
                    }
                }

                // no target column is specified
                if (!AdfDataMapperValidations._isTargetColumnValid(attributeMapping.name)) {
                    violationsDetails.push({
                        violationType: MapperViolationType.TargetColumn,
                        attributeMappingIndex: targetColumnIndex,
                        mappingMethod: attributeMapping.mappingType
                    });
                }

                // no proper source column specified
                else if (!AdfDataMapperValidations._isSourceColumnValid(attributeMapping)) {
                    violationsDetails.push({
                        violationType: MapperViolationType.SourceColumn,
                        attributeMappingIndex: sourceColumnIndex,
                        targetAttribute: attributeMapping.name,
                        mappingMethod: attributeMapping.mappingType
                    });
                }
                else if(isMapperContinuous && attributeMapping.mappingType === MappingType.AGGREGATE){
                    violationsDetails.push({
                        violationType: MapperViolationType.InvalidMethodForContinuousMapper,
                        attributeMappingIndex: targetColumnIndex,
                        targetAttribute: attributeMapping.name
                    });
                }
                else if (isMappingTypeValid) {
                    // validate if the mapped source and target columns are part of source and target schema respectively
                    const numViolations = violationsDetails.length;
                    this._addSourceTargetOrphanAttributesViolations(violationsDetails, attributeMapping, sourceColumnIndex, dataMapperMapping);

                    // check if any new violations were added, if not, set areSourceTargetColumnsProper to true
                    if (numViolations === violationsDetails.length) {
                        areSourceTargetColumnsProper = true;
                    }
                }

                // track if derived (advanced included) transformation was used in mappings
                if (attributeMapping.mappingType === MappingType.DERIVED || attributeMapping.mappingType === MappingType.ADVANCED) {
                    containsDerivedTransformations = true;
                    invalidMappingCombinationAttributeMappingIndex = targetColumnIndex;
                    invalidMappingCombinationAttributeMapping = attributeMapping;
                }
                // track if aggregate transformation was used in mappings
                else if (attributeMapping.mappingType === MappingType.AGGREGATE) {
                    containsAggregateTransformation = true;
                    invalidMappingCombinationAttributeMappingIndex = targetColumnIndex;
                    invalidMappingCombinationAttributeMapping = attributeMapping;
                }

                if (areSourceTargetColumnsProper) {
                    this._addAttributeMappingDataTypeMatchViolations(violationsDetails, attributeMapping, sourceColumnIndex, targetColumnIndex, dataMapperMapping);
                    this._addComplexDataTypeViolations(violationsDetails, attributeMapping, sourceColumnIndex, targetColumnIndex, dataMapperMapping);
                }
            });

            // invalid mapping combination. Dervied and aggregate transformations are not allowed within a single mapping
            if (containsDerivedTransformations && containsAggregateTransformation) {
                violationsDetails.push({
                    violationType: MapperViolationType.MappingCombination,
                    attributeMappingIndex: invalidMappingCombinationAttributeMappingIndex,
                    targetAttribute: invalidMappingCombinationAttributeMapping.name,
                    mappingMethod: invalidMappingCombinationAttributeMapping.mappingType
                });
            }
        }

        return violationsDetails;
    }

    private static _addSourceTargetOrphanAttributesViolations(
        violationsDetails: IMapperViolationDetail[],
        attributeMapping: MapperAttributeMapping,
        index: number,
        dataMapperMapping: DataMapperMapping): void {

        if (attributeMapping.mappingType === MappingType.DIRECT ||
            attributeMapping.mappingType === MappingType.DERIVED ||
            attributeMapping.mappingType === MappingType.AGGREGATE) {
                const sourceDataFlowSource = attributeMapping.attributeReference.table as DataFlowSource;
                AdfDataMapperValidations._addOrphanAttributesViolations(violationsDetails, 'source', index, attributeMapping.attributeReference.columnName, attributeMapping.mappingType, sourceDataFlowSource.schema);
        }
        else if (attributeMapping.mappingType === MappingType.ADVANCED) {
            attributeMapping.attributeReferences?.forEach((attributeReference) => {
                const sourceDataFlowSource = attributeReference.table as DataFlowSource;
                AdfDataMapperValidations._addOrphanAttributesViolations(violationsDetails, 'source', index, attributeReference.columnName, attributeMapping.mappingType, sourceDataFlowSource.schema);
            });
        }

        const targetDataFlowSource = dataMapperMapping.targetTable as DataFlowSource;
        AdfDataMapperValidations._addOrphanAttributesViolations(violationsDetails, 'sink', index, attributeMapping.name, attributeMapping.mappingType, targetDataFlowSource.schema);
    }

    private static _addOrphanAttributesViolations(
        violationsDetails: IMapperViolationDetail[],
        entityType: AdfDataMapperEntityType,
        index: number,
        attributeName: string,
        mappingType: string,
        columns: DataFlowColumn[]): void {

        const matchedCol = columns?.find((col) => col.name === attributeName);

        if (!matchedCol) {
            violationsDetails.push({
                violationType: entityType === 'source' ? MapperViolationType.OrphanSourceColumn : MapperViolationType.OrphanTargetColumn,
                attributeMappingIndex: index,
                targetAttribute: entityType === 'source' ?  undefined : attributeName,
                sourceAttribute: entityType === 'source' ?  attributeName : undefined,
                mappingMethod: mappingType,
                entityType,
            });
        }
    }

    private static _addAttributeMappingDataTypeMatchViolations(
        violationsDetails: IMapperViolationDetail[],
        attributeMapping: MapperAttributeMapping,
        sourceIndex: number,
        targetIndex: number,
        dataMapperMapping: DataMapperMapping): void {

        if (dataMapperMapping?.isValidMapping() !== true) {
            return;
        }

        if (attributeMapping.mappingType === MappingType.DERIVED) {
            const sourceDataFlowSource = attributeMapping.attributeReference.table as DataFlowSource;
            const sourceColumn = sourceDataFlowSource.schema.find((dataFlowColumn) => dataFlowColumn.name === attributeMapping.attributeReference.columnName);
            AdfDataMapperValidations._addColumnDataTypeMatchViolations(violationsDetails, 'source', sourceIndex, dataMapperMapping.sourceTable.tableName, sourceColumn, attributeMapping.functionName, attributeMapping.mappingType, [StringDataFlowType.TYPE]);
        }
        else if (attributeMapping.mappingType === MappingType.AGGREGATE) {
            if (attributeMapping.functionName === DataFlowExpressionFunctions.SUM.name ||
                attributeMapping.functionName === DataFlowExpressionFunctions.MIN.name ||
                attributeMapping.functionName === DataFlowExpressionFunctions.MAX.name ||
                attributeMapping.functionName === DataFlowExpressionFunctions.STDDEV.name ||
                attributeMapping.functionName === DataFlowExpressionFunctions.AVG.name ||
                attributeMapping.functionName === DataFlowExpressionFunctions.MEAN.name) {

                const sourceDataFlowSource = attributeMapping.attributeReference.table as DataFlowSource;
                const sourceColumn = sourceDataFlowSource.schema.find((dataFlowColumn) => dataFlowColumn.name === attributeMapping.attributeReference.columnName);
                const compatibleTypes = [...new LongDataFlowType().compatibleTypes, ...new DecimalDataFlowType().compatibleTypes];
                AdfDataMapperValidations._addColumnDataTypeMatchViolations(violationsDetails, 'source', sourceIndex, dataMapperMapping.targetTable.tableName, sourceColumn, attributeMapping.functionName, attributeMapping.mappingType, compatibleTypes);
            }
        }
    }

    private static _addComplexDataTypeViolations(
        violationsDetails: IMapperViolationDetail[],
        attributeMapping: MapperAttributeMapping,
        sourceColumnIndex: number,
        targetColumnIndex: number,
        dataMapperMapping: DataMapperMapping): void {

        if (!attributeMapping.name  || !dataMapperMapping || dataMapperMapping.targetTable as DataFlowSource === undefined || dataMapperMapping.sourceTable as DataFlowSource === undefined) {
            return;
        }

        const targetDataFlowSource = dataMapperMapping.targetTable as DataFlowSource;
        const targetDataFlowColumn = targetDataFlowSource.schema.find((col) => col.name === attributeMapping.name);
        const isTargetColumnComplex = AdfDataMapperValidations._isDataFlowColumnComplexOrArrayType(targetDataFlowColumn);
        let violationFound = false;

        if (!!targetDataFlowColumn && isTargetColumnComplex) {
            if (!AdfDataMapperValidations.sinksSupportingComplexDataTypes.has(dataMapperMapping.targetTable.datasetType)) {
                violationFound = true;
                violationsDetails.push({
                    violationType: MapperViolationType.InvalidTargetForComplexDataType,
                    attributeMappingIndex: targetColumnIndex,
                    targetTable: dataMapperMapping.targetTable.tableName,
                    targetAttribute: attributeMapping.name,
                    targetDataType: targetDataFlowColumn.dataFlowColumnType?.typeInDsl ?? targetDataFlowColumn.dataFlowColumnType?.typeString,
                    entityType: 'sink',
                    mappingMethod: attributeMapping.mappingType,
                    tableType: dataMapperMapping.targetTable.datasetType
                });
            }
        }

        // TODO: add handling for advanced and complex
        if (!violationFound && (attributeMapping.mappingType === MappingType.DIRECT || attributeMapping.mappingType === MappingType.DERIVED)) {
            const sourceDataFlowSource = dataMapperMapping.sourceTable as DataFlowSource;
            const sourceColumnName = attributeMapping.attributeReference.columnName;
            const sourceDataFlowColumn = sourceDataFlowSource.schema.find((col) => col.name === sourceColumnName);
            const isSourceColumnComplex = AdfDataMapperValidations._isDataFlowColumnComplexOrArrayType(sourceDataFlowColumn);
            if (isTargetColumnComplex || isSourceColumnComplex) {
                const targetDataType = targetDataFlowColumn?.dataFlowColumnType?.typeInDsl;
                const sourceDataType = sourceDataFlowColumn?.dataFlowColumnType?.typeInDsl;
                if (!isTargetColumnComplex || !isSourceColumnComplex || !targetDataType || targetDataType !== sourceDataType) {
                    violationsDetails.push({
                        violationType: MapperViolationType.InvalidMappingForComplexDataType,
                        attributeMappingIndex: sourceColumnIndex,
                        targetTable: dataMapperMapping.targetTable.tableName,
                        targetAttribute: attributeMapping.name,
                        targetDataType: targetDataFlowColumn.dataFlowColumnType?.typeInDsl ?? targetDataFlowColumn.dataFlowColumnType?.typeString,
                        sourceTable: dataMapperMapping.sourceTable.tableName,
                        sourceAttribute: sourceColumnName,
                        sourceDataType: sourceDataFlowColumn.dataFlowColumnType?.typeInDsl ?? sourceDataFlowColumn.dataFlowColumnType?.typeString,
                        entityType: 'source',
                        mappingMethod: attributeMapping.mappingType
                    });
                }
            }
        }
    }

    private static _addColumnDataTypeMatchViolations(
        violationsDetails: IMapperViolationDetail[],
        entityType: AdfDataMapperEntityType,
        index: number,
        tableName: string,
        column: DataFlowColumn,
        functionName: string,
        mappingType: string,
        expectedDataTypes: DataFlowDataType[]): void {

        const columnDataType = column.dataFlowColumnType?.typeString;
        const matchedExpectedType = expectedDataTypes.find((expectedDataType) => expectedDataType.toLowerCase() === columnDataType?.toLowerCase());
        if (!matchedExpectedType) {
            violationsDetails.push({
                violationType: entityType === 'source' ? MapperViolationType.InvalidSourceColumnDataType : MapperViolationType.InvalidTargetColumnDataType,
                attributeMappingIndex: index,
                targetAttribute: entityType === 'source' ?  undefined : column.name,
                sourceAttribute: entityType === 'source' ?  column.name : undefined,
                targetTable: entityType === 'source' ?  undefined : tableName,
                sourceTable: entityType === 'source' ?  tableName : undefined,
                entityType,
                mappingMethod: mappingType,
                functionName,
                sourceDataType: entityType === 'source' ? columnDataType : undefined,
                targetDataType: entityType === 'source' ?  undefined : columnDataType,
                expectedDataTypes
            });
        }
    }

    private static _addDuplicateTargetAttributeMappingViolations(
        violationsDetails: IMapperViolationDetail[],
        dataMapperMapping: DataMapperMapping): void {

        const usedTargetColumnMap = new Map<string, number>();
        dataMapperMapping?.attributeMappings?.forEach((attributeMapping, attributeMappingIndex) => {
            if (!!attributeMapping && !!attributeMapping.name) {
                if (usedTargetColumnMap.has(attributeMapping.name)) {
                    violationsDetails.push({
                        violationType: MapperViolationType.DuplicateTargetMapping,
                        attributeMappingIndex,
                        previousAttributeMappingIndex: usedTargetColumnMap.get(attributeMapping.name),
                        targetAttribute: attributeMapping.name
                    });
                }
                else {
                    usedTargetColumnMap.set(attributeMapping.name, attributeMappingIndex);
                }
            }
        });
    }

    private static _getMatchingTargetConnectionInfo(dataMapperMapping: DataMapperMapping): MappingTargetConnectionInfo {
        if (!!dataMapperMapping.owner && dataMapperMapping.owner instanceof AdfDataMapper) {
            const mapper = dataMapperMapping.owner;
            if (dataMapperMapping.targetConnectionReference?.isValidReference() === true) {
                const matchedTargetConnectionInfo = mapper.targetConnectionsInfo.find((targetConnectionInfo) => {
                    const connectionReference = targetConnectionInfo.connection.getConnectionReference();
                    return MapperConnectionReference.areEqual(connectionReference, dataMapperMapping.targetConnectionReference) ? true : false;
                });

                return matchedTargetConnectionInfo;
            }
        }

        return undefined;
    }

    private static _isAttributeMappingsEmpty(dataMapperMapping: DataMapperMapping): boolean {
        return !dataMapperMapping.attributeMappings || dataMapperMapping.attributeMappings.length === 0;
    }


    private static _isNativeCdcSqlSource(table: IMapperTable, connection: MapperConnection): boolean {
        if (supportedSqlDatasetFormatToDatasetSettingMap.has(table?.datasetType)) {
            const mergedDslProperties =  tableUtils.mergeDictionaries(table?.dslConnectorProperties, connection?.commonDslConnectorProperties);
            return tableUtils.getDslPropertyValue(mergedDslProperties, 'enableNativeCdc') === true;
        }

        return false;
    }


    private static _hasKeysForTarget(table: IMapperTable, connection: MapperConnection): boolean {
        if (tableUtils.supportedPrimaryKeyTargetOptions.has(table?.datasetType)) {
            const mergedDslProperties =  tableUtils.mergeDictionaries(table?.dslConnectorProperties, connection?.commonDslConnectorProperties);
            const keys: string[] = tableUtils.getDslPropertyValue(mergedDslProperties, DslConnectorConstants.keys);
            return !!keys && keys.length > 0;
        }

        return true;
    }

    private static _isTargetColumnValid(targetColumnName: string): boolean {
        return !!targetColumnName;
    }

    private static _isMappingTypeValid(mappingType: string): boolean {
        return !!(mappingType as MappingType);
    }

    private static _isSourceColumnValid(attributeMapping: MapperAttributeMapping): boolean {
        switch (attributeMapping.mappingType) {
            case MappingType.DIRECT:
            case MappingType.DERIVED:
            case MappingType.AGGREGATE:
                if (!AdfDataMapperValidations._isAttributeReferenceValid(attributeMapping.attributeReference)) {
                    return false;
                }
                break;
            case MappingType.ADVANCED:
                if (!AdfDataMapperValidations._isDataFlowExpressionValid(attributeMapping.expression)) {
                    return false;
                }
                break;
            default:
                // fail for unknown mapping method not handled properly in above cases
                return false;
        }

        return true;
    }

    private static _isAttributeReferenceValid(mapperAttributeReference: MapperAttributeReference): boolean {
        if (!mapperAttributeReference ||
            !mapperAttributeReference.columnName ||
            !mapperAttributeReference.table?.tableName ||
            mapperAttributeReference.tableConnectionReference?.isValidReference() !== true) {
            return false;
        }

        return true;
    }

    private static _isDataFlowExpressionValid(dataFlowExpression: IDataFlowExpression): boolean {
        if (!dataFlowExpression || !dataFlowExpression.expression) {
            return false;
        }

        return true;
    }

    private static _isDataFlowColumnComplexOrArrayType(dataFlowColumn: DataFlowColumn): boolean {
        return dataFlowColumn?.dataFlowColumnType?.typeString === 'complex' || dataFlowColumn?.dataFlowColumnType?.typeString === 'array';
    }

    private static _getNumPreviousMappings(
        targetConnectionIndex: number,
        allTargetConnectionsInfo: MappingTargetConnectionInfo[]): number {

        let numPreviousMappings = 0;
        for (let index = 0; index < targetConnectionIndex && index < allTargetConnectionsInfo.length; index++) {
            numPreviousMappings += allTargetConnectionsInfo[index].dataMapperMappings.length;
        }

        return numPreviousMappings;
    }
}
